import React, { useState, useMemo, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { UserContext } from '../../components/UserContext';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { logout as reduxLogout } from '../../store/slices/authSlice';
import { useToast } from '../../components/ToastProvider';
import LoadingOverlay from '../../components/LoadingOverlay';
import MenuSection from '../../components/customer/MenuSection';
import FilterControls from '../../components/customer/FilterControls';
import OrderSummary from '../../components/customer/OrderSummary';
import OrderHistory from '../../components/customer/OrderHistory';
import CustomerProfile from '../../components/customer/CustomerProfile';
import SpecialOffers from '../../components/customer/SpecialOffers';
import CallWaiterButton from '../../components/CallWaiterButton';
import WaiterRequestTracker from '../../components/customer/WaiterRequestTracker';
import OrderEditSection from '../../components/customer/OrderEditSection';
import OrderStatusTracker from '../../components/customer/OrderStatusTracker';
import { useMenuItems } from '../../hooks/useMenuItems';
import { useOrder } from '../../hooks/useOrder';
import { useRestaurantDetails } from '../../hooks/useRestaurantDetails';
import { TABLE_ID, FILTER_OPTIONS } from '../../constants';
import { validateOrderForm } from '../../utils/validation';
import { parseMongoObjectId } from '../../utils/mongoId';
import { API_ENDPOINTS } from '../../config/api';
import { motion } from 'framer-motion';

const CustomerDashboard = () => {
    const context = useContext(UserContext);
    const { user, restaurant, logout } = context || {};
    const dispatch = useAppDispatch();
    const restaurantId = localStorage.getItem('restaurantId') || restaurant?._id || user?.id;
    const tableId = localStorage.getItem('tableId') || TABLE_ID;
    const addToast = useToast();
    const navigate = useNavigate();
    const isLoggingOutRef = useRef(false);
    
    const handleLogout = () => {
        isLoggingOutRef.current = true;
        const loginQuery = new URLSearchParams();
        if (restaurantId) loginQuery.set('restaurantId', restaurantId);
        if (tableId) loginQuery.set('tableId', tableId);
        // Clear Redux auth too — AuthRedirectWrapper reads Redux; without this, "/" redirects back to /customer.
        dispatch(reduxLogout());
        logout();
        navigate(`/customer-login${loginQuery.toString() ? `?${loginQuery.toString()}` : ''}`);
    };
    
    // Custom hooks
    const { menuItems, loading: menuLoading, error: menuError, refetch: refetchMenu, todaysSpecials, quicklyServed, getFilteredItems } = useMenuItems(restaurantId);
    const { order, currentSuggestions, addItemToOrder, removeItem, increaseQuantity, decreaseQuantity, clearOrder, clearSuggestions, totalAmount, serviceCharge } = useOrder();
    const { taxPercentage, calculateTaxAmount } = useRestaurantDetails(restaurantId);
    
    // Local state
    const [customerName, setCustomerName] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [message, setMessage] = useState('');
    const [orderHistory, setOrderHistory] = useState([]);
    /** True after any successful API attempt (including empty result or error after request). */
    const [orderHistoryFetched, setOrderHistoryFetched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [filterCategory, setFilterCategory] = useState(FILTER_OPTIONS.CATEGORY.ALL);
    const [filterType, setFilterType] = useState(FILTER_OPTIONS.TYPE.ALL);
    const [activeTab, setActiveTab] = useState('menu');
    const [tableDetails, setTableDetails] = useState(null);
    const [customerProfile, setCustomerProfile] = useState(null);
    const [restaurantInfo, setRestaurantInfo] = useState(null);
    const [favoriteItems, setFavoriteItems] = useState([]);
    const [appliedOffer, setAppliedOffer] = useState(null);
    const [offerDiscount, setOfferDiscount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);
    const [showOrderHistory, setShowOrderHistory] = useState(false);
    const [showSpecialOffers, setShowSpecialOffers] = useState(false);
    const [showOrderConfirmModal, setShowOrderConfirmModal] = useState(false);
    /** Search + filters — hidden by default on Menu tab */
    const [showMenuTools, setShowMenuTools] = useState(false);
    /** null = loading; true/false after /offers/active fetch (hide UI when empty) */
    const [hasRestaurantSpecialOffers, setHasRestaurantSpecialOffers] = useState(null);
    /** Increment to force OrderStatusTracker to refetch (Refresh all button). */
    const [ordersRefreshTrigger, setOrdersRefreshTrigger] = useState(0);

    // Memoized filtered items with search
    const filteredMenuItems = useMemo(() => {
        let items = getFilteredItems(filterCategory, filterType);
        
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            items = items.filter(item => 
                item.itemName.toLowerCase().includes(query) ||
                (item.itemDescription && item.itemDescription.toLowerCase().includes(query)) ||
                item.itemCategory.toLowerCase().includes(query)
            );
        }
        
        return items;
    }, [getFilteredItems, filterCategory, filterType, searchQuery]);

    
    // Search filtered special sections
    const searchFilteredTodaysSpecials = useMemo(() => {
        if (!searchQuery.trim()) return todaysSpecials;
        const query = searchQuery.toLowerCase();
        return todaysSpecials.filter(item => 
            item.itemName.toLowerCase().includes(query) ||
            (item.itemDescription && item.itemDescription.toLowerCase().includes(query)) ||
            item.itemCategory.toLowerCase().includes(query)
        );
    }, [todaysSpecials, searchQuery]);
    
    const searchFilteredQuicklyServed = useMemo(() => {
        if (!searchQuery.trim()) return quicklyServed;
        const query = searchQuery.toLowerCase();
        return quicklyServed.filter(item => 
            item.itemName.toLowerCase().includes(query) ||
            (item.itemDescription && item.itemDescription.toLowerCase().includes(query)) ||
            item.itemCategory.toLowerCase().includes(query)
        );
    }, [quicklyServed, searchQuery]);
    
    const taxAmount = useMemo(() => 
        calculateTaxAmount(totalAmount), 
        [calculateTaxAmount, totalAmount]
    );

    const totalItemsInOrder = useMemo(
        () => order.reduce((sum, item) => sum + (item.quantity || 0), 0),
        [order]
    );

    useEffect(() => {
        if (!restaurantId) {
            setHasRestaurantSpecialOffers(false);
            return;
        }
        let cancelled = false;
        axios
            .get(`${API_ENDPOINTS.OFFERS}/active/${restaurantId}`)
            .then((res) => {
                if (cancelled) return;
                const list = Array.isArray(res.data) ? res.data : [];
                setHasRestaurantSpecialOffers(list.length > 0);
            })
            .catch(() => {
                if (!cancelled) {
                    // Keep section visible so SpecialOffers can use client-side fallback offers
                    setHasRestaurantSpecialOffers(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [restaurantId]);

    useEffect(() => {
        if (activeTab !== 'orders') {
            setOrdersRefreshTrigger(0);
        }
    }, [activeTab]);

    // Occupy table and load customer data
    useEffect(() => {
        occupyTable();
        
        // Free table when user leaves browser/tab
        const handleBeforeUnload = () => {
            if (tableId) {
                navigator.sendBeacon(API_ENDPOINTS.TABLE_FREE(tableId), '');
            }
        };
        
        window.addEventListener('beforeunload', handleBeforeUnload);

        const storedUserData = JSON.parse(localStorage.getItem('userData'));
        const storedMobile = storedUserData?.phoneNumber;
        const storedName = storedUserData?.name;
        if (storedMobile) {
            setContactNumber(storedMobile);
            fetchCustomerProfile(storedMobile);
            fetchOrderHistory(storedMobile);
        }
        if (storedName) {
            setCustomerName(storedName);
        }
        
        // Restore guest order if available
        const restoreOrder = localStorage.getItem('restoreOrder');
        const restoreCustomerInfo = localStorage.getItem('restoreCustomerInfo');
        
        if (restoreOrder) {
            try {
                const guestOrder = JSON.parse(restoreOrder);
                const guestInfo = JSON.parse(restoreCustomerInfo || '{}');
                
                // Restore order items
                guestOrder.forEach(item => {
                    addItemToOrder(item);
                });
                
                // Restore customer info if not already set
                if (guestInfo.customerName && !storedName) {
                    setCustomerName(guestInfo.customerName);
                }
                if (guestInfo.contactNumber && !storedMobile) {
                    setContactNumber(guestInfo.contactNumber);
                }
                if (guestInfo.message) {
                    setMessage(guestInfo.message);
                }
                
                // Clean up restore data
                localStorage.removeItem('restoreOrder');
                localStorage.removeItem('restoreCustomerInfo');
                
                addToast('Your previous order has been restored!', 'success');
            } catch (error) {
                console.error('Failed to restore guest order:', error);
            }
        }
        
        fetchTableDetails();
        fetchRestaurantInfo();
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // Skip freeing table during logout flow to avoid duplicate calls/race conditions
            if (!isLoggingOutRef.current) {
                freeTable();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    // Occupy table when customer accesses menu
    const occupyTable = async () => {
        try {
            if (tableId) {
                await axios.put(API_ENDPOINTS.TABLE_OCCUPY(tableId));
            }
        } catch (error) {
            console.error('Failed to occupy table:', error);
        }
    };
    
    // Free table when customer leaves
    const freeTable = async () => {
        try {
            if (tableId) {
                await axios.put(API_ENDPOINTS.TABLE_FREE(tableId));
            }
        } catch (error) {
            console.error('Failed to free table:', error);
        }
    };

    // Fetch table details
    const fetchTableDetails = async () => {
        try {
            if (tableId) {
                const response = await axios.get(API_ENDPOINTS.CUSTOMER_TABLE_DETAILS(tableId));
                setTableDetails(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch table details:', error);
            // Fallback to getting all tables and finding the one
            try {
                const response = await axios.get(`${API_ENDPOINTS.TABLES(restaurantId)}`);
                const table = response.data.find(t => t._id === tableId);
                setTableDetails(table);
            } catch (fallbackError) {
                console.error('Fallback table fetch failed:', fallbackError);
            }
        }
    };

    // Fetch restaurant info
    const fetchRestaurantInfo = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.RESTAURANT_BY_ID(restaurantId));
            setRestaurantInfo(response.data);
        } catch (error) {
            console.error('Failed to fetch restaurant info:', error);
        }
    };

    // Fetch customer profile
    const fetchCustomerProfile = async (mobile) => {
        try {
            const response = await axios.get(API_ENDPOINTS.CUSTOMER_PROFILE(mobile));
            setCustomerProfile(response.data);
            // Extract favorite item IDs
            const favIds = response.data?.preferences?.favoriteItems?.map(item => item._id) || [];
            setFavoriteItems(favIds);
        } catch (error) {
            console.error('Failed to fetch customer profile:', error);
        }
    };

    // Handle favorite change
    const handleFavoriteChange = (itemId, isFavorite) => {
        if (isFavorite) {
            setFavoriteItems(prev => [...prev, itemId]);
        } else {
            setFavoriteItems(prev => prev.filter(id => id !== itemId));
        }
    };

    // Handle offer application
    const handleApplyOffer = async (offer) => {
        try {
            const response = await axios.post(`${API_ENDPOINTS.OFFERS}/apply`, {
                offerId: offer._id,
                orderAmount: totalAmount,
                customerData: customerProfile
            });
            
            setAppliedOffer(offer);
            setOfferDiscount(response.data.discountAmount);
            addToast(`Offer applied! You saved ₹${response.data.discountAmount}`, 'success');
        } catch (error) {
            addToast(error.response?.data?.message || 'Failed to apply offer', 'error');
        }
    };

    // Remove applied offer
    const removeAppliedOffer = () => {
        setAppliedOffer(null);
        setOfferDiscount(0);
        addToast('Offer removed', 'info');
    };

    // Order management functions are now handled by useOrder hook

    // Place Order: call backend endpoint to create order.
    const placeOrderInternal = async () => {
        const validation = validateOrderForm(customerName, contactNumber, order);
        if (!validation.isValid) {
            addToast(validation.errors.join(', '), "warning");
            return;
        }
        
        try {
            setLoading(true);
            const finalAmount = Number(totalAmount) + Number(serviceCharge) + Number(taxAmount) - Number(offerDiscount);
            const resolvedTableId = parseMongoObjectId(tableId);
            const payload = {
                restaurantId: restaurantId,
                customerName,
                mobileNumber: contactNumber || null,
                message,
                items: order,
                serviceAmount: serviceCharge,
                taxPercentage: taxAmount,
                appliedOffer: appliedOffer?._id,
                offerDiscount,
                finalAmount,
                orderDate: new Date(),
                orderType: resolvedTableId ? 'table' : 'counter',
                ...(resolvedTableId ? { tableId: resolvedTableId } : {}),
            };
            await axios.post(API_ENDPOINTS.ORDERS_ADD, payload);
            
            // Record offer usage if applied
            if (appliedOffer) {
                await axios.post(`${API_ENDPOINTS.OFFERS}/use/${appliedOffer._id}`);
            }
            
            addToast("Order placed successfully!", "success");
            
            // Store customer data for future use (only if contact number is provided)
            if (contactNumber) {
                localStorage.setItem('customerMobile', contactNumber);
            }
            localStorage.setItem('customerName', customerName);
            
            // Clear form and order
            clearOrder();
            setMessage('');
            setAppliedOffer(null);
            setOfferDiscount(0);
            // Only fetch order history if contact number is provided
            if (contactNumber) {
                fetchOrderHistory(contactNumber);
            }
        } catch (error) {
            addToast("Failed to place order", "error");
            console.error('Order placement error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handler for "Order" buttons – first show confirmation modal with summary
    const openOrderConfirmation = () => {
        const validation = validateOrderForm(customerName, contactNumber, order);
        if (!validation.isValid) {
            addToast(validation.errors.join(', '), 'warning');
            return;
        }
        if (order.length === 0) {
            addToast('Please add at least one item to your order.', 'warning');
            return;
        }
        setShowOrderConfirmModal(true);
    };

    // Function to fetch order history using contactNumber and restaurantId
    const fetchOrderHistory = async (mobile = contactNumber, bustCache = false) => {
        if (!mobile?.trim()) {
            addToast("Please enter your contact number to view order history.", "warning");
            return;
        }
        
        try {
            setLoading(true);
            const baseUrl = API_ENDPOINTS.CUSTOMER_ORDER_HISTORY(restaurantId, mobile.trim(), null);
            const url = bustCache ? `${baseUrl}&_cb=${Date.now()}` : baseUrl;
            const response = await axios.get(url);
            setOrderHistory(response.data);
        } catch (err) {
            console.error("Failed to load order history", err);
            addToast("Failed to load order history", "error");
        } finally {
            setLoading(false);
            setOrderHistoryFetched(true);
        }
    };

    // Tab content renderer
    const renderTabContent = () => {
        switch (activeTab) {
            case 'menu':
                return (
                    <div className="space-y-8">
                        {hasRestaurantSpecialOffers === true && (
                            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 sm:p-4">
                                <div className="flex items-center justify-between">
                                    {!showSpecialOffers && (
                                        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                                            Special Offers
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setShowSpecialOffers((prev) => !prev)}
                                        className="inline-flex items-center justify-center rounded-full border border-stone-200 bg-white px-3 py-1 text-[11px] font-semibold text-stone-600 hover:bg-stone-50 transition-colors"
                                        aria-label={showSpecialOffers ? 'Hide special offers' : 'Show special offers'}
                                    >
                                        {showSpecialOffers ? 'Hide' : 'Show'}
                                    </button>
                                </div>
                                {showSpecialOffers && (
                                    <SpecialOffers 
                                        customerProfile={customerProfile}
                                        restaurantInfo={restaurantInfo}
                                        orderHistory={orderHistory}
                                        onApplyOffer={handleApplyOffer}
                                    />
                                )}
                            </div>
                        )}
                        
                        <MenuSection 
                            title="Quickly Served" 
                            items={searchFilteredQuicklyServed} 
                            onAddToOrder={addItemToOrder}
                            customerPhone={contactNumber}
                            favoriteItems={favoriteItems}
                            onFavoriteChange={handleFavoriteChange}
                        />

                        <MenuSection 
                            title="Today's Specials" 
                            items={searchFilteredTodaysSpecials} 
                            onAddToOrder={addItemToOrder}
                            customerPhone={contactNumber}
                            favoriteItems={favoriteItems}
                            onFavoriteChange={handleFavoriteChange}
                        />

                        {searchQuery.trim() && (
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-blue-600 text-lg">🔍</span>
                                        <span className="text-blue-800 font-medium">
                                            Search results for "{searchQuery}"
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                                    >
                                        Clear search
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        <MenuSection 
                            title="Menu" 
                            items={filteredMenuItems} 
                            onAddToOrder={addItemToOrder}
                            customerPhone={contactNumber}
                            favoriteItems={favoriteItems}
                            onFavoriteChange={handleFavoriteChange}
                            headerAction={
                                <button
                                    type="button"
                                    aria-expanded={showMenuTools}
                                    aria-controls="customer-menu-tools"
                                    onClick={() => setShowMenuTools((v) => !v)}
                                    className="px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                    {showMenuTools ? 'Hide' : 'Show'}
                                </button>
                            }
                            belowTitle={
                                showMenuTools ? (
                                    <div id="customer-menu-tools" className="space-y-3">
                                        <div className="relative w-full max-w-md">
                                            <input
                                                type="text"
                                                placeholder="Search menu items..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="w-full pl-10 pr-9 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all bg-white text-sm text-slate-800"
                                            />
                                            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                                                🔍
                                            </div>
                                            {searchQuery && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSearchQuery('')}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                        <FilterControls 
                                            filterCategory={filterCategory}
                                            filterType={filterType}
                                            onCategoryChange={setFilterCategory}
                                            onTypeChange={setFilterType}
                                        />
                                    </div>
                                ) : null
                            }
                        />

                        {/* Show dynamic suggestions when items are added to cart */}
                        {currentSuggestions?.length > 0 && (
                            <div className="mt-6">
                                <MenuSection 
                                    title="You might also like" 
                                    items={currentSuggestions} 
                                    onAddToOrder={addItemToOrder}
                                    customerPhone={contactNumber}
                                    favoriteItems={favoriteItems}
                                    onFavoriteChange={handleFavoriteChange}
                                />
                                <motion.button 
                                    onClick={clearSuggestions} 
                                    className="mt-4 px-6 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all duration-300 font-semibold"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    Clear Suggestions
                                </motion.button>
                            </div>
                        )}
                    </div>
                );
            case 'orders':
                return (
                    <div className="space-y-4 sm:space-y-6">
                        {/* Header with Refresh Button */}
                        <div className="flex items-center justify-between gap-2">
                            <div>
                                <h2 className="text-lg sm:text-2xl font-bold text-stone-900 flex items-center gap-2">
                                    <span className="text-xl">📋</span>
                                    <span>My orders</span>
                                </h2>
                                <p className="text-xs sm:text-sm text-stone-500 mt-0.5">
                                    Track your active and past orders in one place.
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    fetchOrderHistory(undefined, true);
                                    if (contactNumber) {
                                        setOrdersRefreshTrigger((k) => k + 1);
                                    }
                                }}
                                disabled={loading}
                                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 transition-colors"
                                title="Refresh all orders"
                            >
                                <span className={loading ? 'animate-spin' : ''}>🔄</span>
                                <span className="hidden sm:inline">{loading ? 'Refreshing…' : 'Refresh all'}</span>
                            </button>
                        </div>

                        {/* Order Status Tracker for Active Orders */}
                        {contactNumber && (
                            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 sm:p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                                        Active orders
                                    </p>
                                    <span className="text-[11px] text-stone-400">
                                        Live updates enabled
                                    </span>
                                </div>
                                <OrderStatusTracker
                                    contactNumber={contactNumber}
                                    restaurantId={restaurantId}
                                    tableId={tableId}
                                    refreshTrigger={ordersRefreshTrigger}
                                />
                            </div>
                        )}
                        
                        {editingOrder && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50/60 shadow-sm p-3 sm:p-4">
                                <OrderEditSection
                                    order={editingOrder}
                                    onOrderUpdate={(updatedOrder) => {
                                        fetchOrderHistory();
                                        setEditingOrder(null);
                                    }}
                                    onClose={() => setEditingOrder(null)}
                                />
                            </div>
                        )}

                        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 sm:p-4">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                                    Order history
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setShowOrderHistory((prev) => !prev)}
                                    className="inline-flex items-center justify-center rounded-full border border-stone-200 bg-white px-3 py-1 text-[11px] font-semibold text-stone-600 hover:bg-stone-50 transition-colors"
                                    aria-label={showOrderHistory ? 'Hide order history' : 'Show order history'}
                                >
                                    {showOrderHistory ? 'Hide' : 'Show'}
                                </button>
                            </div>
                            {showOrderHistory && (
                                <OrderHistory 
                                    orderHistory={orderHistory}
                                    contactNumber={contactNumber}
                                    onContactNumberChange={setContactNumber}
                                    onLoadHistory={() => fetchOrderHistory()}
                                    loading={loading}
                                    onEditOrder={setEditingOrder}
                                    orderHistoryFetched={orderHistoryFetched}
                                />
                            )}
                        </div>
                    </div>
                );
            case 'requests':
                return (
                    <WaiterRequestTracker 
                        customerMobile={contactNumber}
                        customerName={customerName}
                        restaurantId={restaurantId}
                    />
                );
            case 'profile':
                return (
                    <CustomerProfile 
                        phoneNumber={contactNumber || user?.phoneNumber}
                        restaurantId={restaurantId}
                    />
                );
            case 'offers':
                return (
                    <div className="space-y-8">
                        {hasRestaurantSpecialOffers === true && (
                            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 sm:p-4">
                                <div className="flex items-center justify-between mb-3">
                                    {!showSpecialOffers && (
                                        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                                            Special Offers
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setShowSpecialOffers((prev) => !prev)}
                                        className="inline-flex items-center justify-center rounded-full border border-stone-200 bg-white px-3 py-1 text-[11px] font-semibold text-stone-600 hover:bg-stone-50 transition-colors"
                                        aria-label={showSpecialOffers ? 'Hide special offers' : 'Show special offers'}
                                    >
                                        {showSpecialOffers ? 'Hide' : 'Show'}
                                    </button>
                                </div>
                                {showSpecialOffers && (
                                    <SpecialOffers 
                                        customerProfile={customerProfile}
                                        restaurantInfo={restaurantInfo}
                                        orderHistory={orderHistory}
                                        onApplyOffer={handleApplyOffer}
                                    />
                                )}
                            </div>
                        )}
                        
                        {/* Referral Program */}
                        <motion.div 
                            className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-8 text-white"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-3xl font-bold mb-2">🤝 Refer & Earn</h3>
                                    <p className="text-blue-100">Invite friends and earn rewards together!</p>
                                </div>
                                <div className="text-6xl">💰</div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-white/20 rounded-xl p-4">
                                    <h4 className="font-bold mb-2">You Get</h4>
                                    <p className="text-2xl font-bold">₹50 Credit</p>
                                    <p className="text-blue-100 text-sm">When friend places first order</p>
                                </div>
                                <div className="bg-white/20 rounded-xl p-4">
                                    <h4 className="font-bold mb-2">Friend Gets</h4>
                                    <p className="text-2xl font-bold">20% Off</p>
                                    <p className="text-blue-100 text-sm">On their first order</p>
                                </div>
                            </div>
                            
                            <motion.button
                                className="w-full mt-6 bg-white text-blue-600 font-bold py-3 px-6 rounded-xl hover:bg-blue-50 transition-all duration-300"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Share Referral Code
                            </motion.button>
                        </motion.div>
                        
                        {/* Feedback Section */}
                        <motion.div 
                            className="bg-white rounded-2xl shadow-xl p-8"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <h3 className="text-2xl font-bold mb-6 text-gray-800 flex items-center space-x-2">
                                <span>💬</span>
                                <span>Share Your Feedback</span>
                            </h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <motion.button
                                    className="bg-gradient-to-r from-green-400 to-green-600 text-white p-6 rounded-xl font-semibold hover:from-green-500 hover:to-green-700 transition-all duration-300"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <div className="text-3xl mb-2">😊</div>
                                    <div>Great Experience</div>
                                </motion.button>
                                
                                <motion.button
                                    className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white p-6 rounded-xl font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all duration-300"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <div className="text-3xl mb-2">😐</div>
                                    <div>Could Be Better</div>
                                </motion.button>
                                
                                <motion.button
                                    className="bg-gradient-to-r from-red-400 to-red-600 text-white p-6 rounded-xl font-semibold hover:from-red-500 hover:to-red-700 transition-all duration-300"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <div className="text-3xl mb-2">😞</div>
                                    <div>Need Improvement</div>
                                </motion.button>
                            </div>
                        </motion.div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Layout hideHeaderFooter={true}>
            {(loading || menuLoading) && <LoadingOverlay />}
            
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-50">
                {/* Header (match Restaurant dashboard mobile pattern) */}
                <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-sm">
                    <div className="w-full px-3 sm:px-4 lg:px-6 py-3">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                {restaurantInfo?.logo && (
                                    <img
                                        src={restaurantInfo.logo}
                                        alt={restaurantInfo.name}
                                        className="h-10 w-10 rounded-xl shadow-sm object-cover border border-slate-200 bg-white flex-shrink-0"
                                    />
                                )}
                                <div className="min-w-0">
                                    <h1 className="text-base sm:text-lg font-bold text-slate-800 truncate">
                                        {restaurantInfo?.name || 'Restaurant'}
                                    </h1>
                                    <p className="text-xs text-slate-600 truncate">
                                        🪑 Table:{' '}
                                        <span className="font-semibold text-slate-700">
                                            {tableDetails?.displayName || tableDetails?.tableName || tableDetails?.tableNumber || '—'}
                                        </span>
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={openOrderConfirmation}
                                    disabled={loading || order.length === 0}
                                    className="inline-flex items-center justify-center rounded-xl bg-stone-900 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-stone-800 disabled:bg-stone-300 disabled:text-stone-600 transition-colors"
                                >
                                    <span className="mr-1" aria-hidden>🛒</span>
                                    <span className="hidden sm:inline">
                                        Order{totalItemsInOrder > 0 ? ` (${totalItemsInOrder})` : ''}
                                    </span>
                                    <span className="sm:hidden">
                                        {totalItemsInOrder > 0 ? totalItemsInOrder : '0'}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    onClick={refetchMenu}
                                    disabled={menuLoading}
                                    aria-label="Refresh menu"
                                    title="Refresh menu"
                                    className="inline-flex items-center justify-center gap-1 rounded-xl border border-slate-200 bg-white px-2.5 sm:px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <span className={menuLoading ? 'inline-block animate-spin' : ''} aria-hidden>🔄</span>
                                    <span className="text-[11px] sm:text-xs">Refresh</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                    🚪
                                    <span className="hidden sm:inline ml-1">Logout</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation (match Restaurant dashboard mobile pattern) */}
                <div className="w-full lg:mt-2 px-0 sm:px-4 lg:px-6 py-3 sm:py-4">
                    <div className="mb-0 sm:mb-6 overflow-hidden sm:bg-white sm:rounded-xl sm:shadow-lg sm:border sm:border-slate-200/80">
                        <div className="sm:border-0 sm:rounded-none sm:shadow-none sm:bg-transparent sm:border-b sm:border-slate-200">
                            <div
                                className="flex sm:flex-wrap overflow-x-auto sm:overflow-visible scroll-smooth gap-1.5 sm:gap-0 p-2 sm:p-0 bg-slate-100/90 rounded-none sm:rounded-none"
                                style={{ WebkitOverflowScrolling: 'touch' }}
                            >
                                {[
                                    { id: 'menu', label: 'Menu', shortLabel: 'Menu', icon: '🍽️' },
                                    { id: 'orders', label: 'My Orders', shortLabel: 'Orders', icon: '📋' },
                                    { id: 'requests', label: 'My Requests', shortLabel: 'Requests', icon: '🔔' },
                                    { id: 'profile', label: 'Profile', shortLabel: 'Profile', icon: '👤' },
                                    { id: 'offers', label: 'Offers', shortLabel: 'Offers', icon: '🎁' }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`
                                            flex-shrink-0 transition-all duration-300 min-h-[52px] sm:min-h-0
                                            flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-0.5 sm:gap-2
                                            px-4 py-2.5 sm:px-5 sm:py-4 font-medium whitespace-nowrap
                                            rounded-xl sm:rounded-none border-l-0 sm:border-l-0 border-b-2 sm:border-b-2 border-transparent
                                            ${activeTab === tab.id
                                                ? 'bg-white shadow-md border-slate-200/60 text-slate-800 sm:bg-inherit sm:shadow-none sm:border-orange-500 sm:text-orange-600'
                                                : 'text-slate-500 active:bg-white/50 sm:text-gray-500 sm:hover:text-orange-600 sm:border-transparent'
                                            }
                                        `}
                                    >
                                        <span className="text-[22px] sm:text-lg leading-none">{tab.icon}</span>
                                        <span className="text-[11px] sm:text-base hidden sm:inline font-medium">{tab.label}</span>
                                        <span className="text-[11px] sm:hidden font-medium">{tab.shortLabel}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="px-3 py-4 sm:p-6 bg-white sm:rounded-b-xl">
                            {menuError && (
                                <motion.div 
                                    className="mx-3 sm:mx-0 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6"
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    {menuError}
                                </motion.div>
                            )}
                            
                            {renderTabContent()}
                        </div>
                    </div>

                    {/* Enhanced Order Summary - Always Visible */}
                    {order.length > 0 && (
                        <motion.div 
                            className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-slate-200/80 p-3 sm:p-4 lg:p-6 xl:p-8 mb-4 sm:mb-6 lg:mb-8"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4 lg:mb-6 text-gray-800 flex items-center space-x-2">
                                <span className="text-base sm:text-lg lg:text-xl">🛒</span>
                                <span>Your Order</span>
                            </h2>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-3 sm:mb-4 lg:mb-6">
                                <div>
                                    <label className="block font-semibold mb-1 sm:mb-2 text-gray-700 text-sm sm:text-base">Name *</label>
                                    <input
                                        type="text"
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        className="w-full border-2 border-gray-200 p-2 sm:p-3 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                                        placeholder="Enter your name"
                                    />
                                </div>
                                <div>
                                    <label className="block font-semibold mb-1 sm:mb-2 text-gray-700 text-sm sm:text-base">Contact Number *</label>
                                    <input
                                        type="tel"
                                        value={contactNumber}
                                        onChange={(e) => setContactNumber(e.target.value)}
                                        className="w-full border-2 border-gray-200 p-2 sm:p-3 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                                        placeholder="Enter 10-digit mobile number"
                                        maxLength="10"
                                    />
                                </div>
                            </div>
                            
                            <div className="mb-3 sm:mb-4 lg:mb-6">
                                <label className="block font-semibold mb-1 sm:mb-2 text-gray-700 text-sm sm:text-base">Special Instructions</label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    className="w-full border-2 border-gray-200 p-2 sm:p-3 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
                                    placeholder="Any special requests or dietary requirements?"
                                    rows="2"
                                />
                            </div>
                            
                            {appliedOffer && (
                                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h4 className="font-semibold text-green-800">{appliedOffer.title}</h4>
                                            <p className="text-green-600 text-sm">Discount: ₹{offerDiscount}</p>
                                        </div>
                                        <button
                                            onClick={removeAppliedOffer}
                                            className="text-red-500 hover:text-red-700 font-semibold"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            <OrderSummary 
                                order={order}
                                totalAmount={totalAmount}
                                serviceCharge={serviceCharge}
                                taxPercentage={taxPercentage}
                                taxAmount={taxAmount}
                                offerDiscount={offerDiscount}
                                onIncreaseQuantity={increaseQuantity}
                                onDecreaseQuantity={decreaseQuantity}
                                onRemoveItem={removeItem}
                            />
                            
                            <motion.button
                                onClick={openOrderConfirmation}
                                disabled={loading || order.length === 0}
                                className="w-full bg-stone-900 hover:bg-stone-800 text-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-lg sm:rounded-xl mt-3 sm:mt-4 lg:mt-6 font-bold text-sm sm:text-base lg:text-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-lg"
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center space-x-2">
                                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                                        <span className="hidden xs:inline">Placing Order...</span>
                                        <span className="xs:hidden">Placing...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center space-x-2">
                                        <span className="text-sm sm:text-base">🚀</span>
                                        <span className="hidden xs:inline">Place Order</span>
                                        <span className="xs:hidden">Order</span>
                                    </div>
                                )}
                            </motion.button>
                        </motion.div>
                    )}
                </div>
            </div>
            
            {/* Order confirmation modal */}
            {showOrderConfirmModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-3 sm:p-4">
                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl border border-stone-200 max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-stone-200 bg-stone-50/90">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500">Review your order</p>
                                <p className="text-sm font-semibold text-stone-800 mt-0.5">
                                    {totalItemsInOrder} item{totalItemsInOrder !== 1 ? 's' : ''} · Table {tableDetails?.displayName || tableDetails?.tableNumber || tableDetails?.tableName || '—'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowOrderConfirmModal(false)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 hover:bg-stone-100 hover:text-stone-800 text-sm"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4 space-y-4">
                            <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs sm:text-sm text-stone-700">
                                <p><span className="font-semibold">Name:</span> {customerName || '—'}</p>
                                <p><span className="font-semibold">Contact:</span> {contactNumber || '—'}</p>
                                {message && (
                                    <p className="mt-1">
                                        <span className="font-semibold">Instructions:</span> {message}
                                    </p>
                                )}
                            </div>

                            <OrderSummary
                                order={order}
                                totalAmount={totalAmount}
                                serviceCharge={serviceCharge}
                                taxPercentage={taxPercentage}
                                taxAmount={taxAmount}
                                offerDiscount={offerDiscount}
                                onIncreaseQuantity={increaseQuantity}
                                onDecreaseQuantity={decreaseQuantity}
                                onRemoveItem={removeItem}
                            />
                        </div>

                        <div className="border-t border-stone-200 bg-white px-4 py-2 sm:px-5 sm:py-2.5 flex gap-1.5">
                            <button
                                type="button"
                                onClick={() => setShowOrderConfirmModal(false)}
                                className="flex-1 inline-flex items-center justify-center rounded-full border border-stone-200 bg-white px-3 py-1.5 min-h-9 text-xs sm:text-sm font-semibold text-stone-700 hover:bg-stone-50 transition-colors"
                            >
                                Review again
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    await placeOrderInternal();
                                    setShowOrderConfirmModal(false);
                                }}
                                disabled={loading}
                                className="flex-1 inline-flex items-center justify-center rounded-full bg-stone-900 px-3 py-1.5 min-h-9 text-xs sm:text-sm font-semibold text-white shadow-sm hover:bg-stone-800 disabled:bg-stone-400 transition-colors whitespace-nowrap"
                            >
                                {loading ? (
                                    'Placing...'
                                ) : (
                                    <>
                                        <span className="sm:hidden">Place order</span>
                                        <span className="hidden sm:inline">Confirm & place order</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Call Waiter Button */}
            <CallWaiterButton 
                restaurantId={restaurantId}
                tableId={tableId}
                customerName={customerName}
                customerMobile={contactNumber}
            />
        </Layout>
    );
};

export default CustomerDashboard;